import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useState, useRef } from 'react';
import { string, shape, object, arrayOf } from 'prop-types';
var matchMedia = globalThis.matchMedia;
/**
 * A hook that will return all matched styles for any given media queries using the
 * matchMedia API (https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia)
 *
 * @param {Object} props
 * @param {Array} props.mediaQueries The array of media rules and respective styles to apply
 *
 * @returns {Object}
 */

export var useMediaQuery = function useMediaQuery() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    mediaQueries: []
  };

  var _useState = useState({}),
      _useState2 = _slicedToArray(_useState, 2),
      styles = _useState2[0],
      setStyles = _useState2[1];

  var isMountedRef = useRef(null);
  var mediaQueries = props.mediaQueries;
  useEffect(function () {
    isMountedRef.current = true;
    if (!mediaQueries) return;
    var mqlList = mediaQueries.map(function (_ref) {
      var media = _ref.media;
      return matchMedia(media);
    });

    var handleMatch = function handleMatch(query, i) {
      if (!isMountedRef.current) return;

      if (query.matches) {
        setStyles(function (prevState) {
          return _objectSpread({}, prevState, mediaQueries[i].style);
        });
      } else {
        setStyles(function (prevState) {
          var filteredState = Object.keys(prevState).filter(function (key) {
            return mediaQueries[i].style[key] !== prevState[key];
          }).reduce(function (obj, key) {
            return _objectSpread({}, obj, _defineProperty({}, key, prevState[key]));
          }, {});
          return filteredState;
        });
      }
    };

    mqlList.forEach(function (mql, i) {
      if (mql.matches) {
        setStyles(function (prevState) {
          return _objectSpread({}, prevState, mediaQueries[i].style);
        });
      }

      mql.addEventListener('change', function (query) {
        return handleMatch(query, i);
      });
    });
    return function () {
      isMountedRef.current = false;
      mqlList.forEach(function (mql, i) {
        mql.removeEventListener('change', function (query) {
          return handleMatch(query, i);
        });
      });
    };
  }, [mediaQueries]);
  return {
    styles: styles
  };
};
useMediaQuery.propTypes = {
  mediaQueries: arrayOf(shape({
    media: string.isRequired,
    style: object.isRequired
  })).isRequired
};